import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface BadgeVisibilitySettings {
  heroBadge: boolean;
  aboutBadge: boolean;
  animationBadge: boolean;
  testimonialsBadge: boolean;
  launchBadges: boolean;
  productBadges: boolean;
  wikiBadges: boolean;
  contactBadge: boolean;
  faqBadge: boolean;
  legalBadges: boolean;
  footerWarrantyBadges: boolean;
}

type BadgeVisibilityKey = keyof BadgeVisibilitySettings;

interface BadgeVisibilityContextValue {
  settings: BadgeVisibilitySettings;
  allEnabled: boolean;
  setBadgeVisibility: (key: BadgeVisibilityKey, value: boolean) => void;
  setAllBadges: (value: boolean) => void;
  resetBadgeVisibility: () => void;
}

const STORAGE_KEY = 'stingray_badge_visibility';

const DEFAULT_SETTINGS: BadgeVisibilitySettings = {
  heroBadge: true,
  aboutBadge: true,
  animationBadge: true,
  testimonialsBadge: true,
  launchBadges: true,
  productBadges: true,
  wikiBadges: true,
  contactBadge: true,
  faqBadge: true,
  legalBadges: true,
  footerWarrantyBadges: true,
};

const BadgeVisibilityContext = createContext<BadgeVisibilityContextValue | null>(null);

function normalizeSettings(raw: Partial<BadgeVisibilitySettings> | null | undefined): BadgeVisibilitySettings {
  const providedValues = Object.values(raw ?? {}).filter((value): value is boolean => typeof value === 'boolean');
  const missingFallback =
    providedValues.length > 0 && providedValues.every((value) => value === false)
      ? false
      : providedValues.length > 0 && providedValues.every((value) => value === true)
        ? true
        : undefined;

  return {
    heroBadge: raw?.heroBadge ?? DEFAULT_SETTINGS.heroBadge,
    aboutBadge: raw?.aboutBadge ?? (missingFallback ?? DEFAULT_SETTINGS.aboutBadge),
    animationBadge: raw?.animationBadge ?? (missingFallback ?? DEFAULT_SETTINGS.animationBadge),
    testimonialsBadge: raw?.testimonialsBadge ?? (missingFallback ?? DEFAULT_SETTINGS.testimonialsBadge),
    launchBadges: raw?.launchBadges ?? (missingFallback ?? DEFAULT_SETTINGS.launchBadges),
    productBadges: raw?.productBadges ?? (missingFallback ?? DEFAULT_SETTINGS.productBadges),
    wikiBadges: raw?.wikiBadges ?? (missingFallback ?? DEFAULT_SETTINGS.wikiBadges),
    contactBadge: raw?.contactBadge ?? (missingFallback ?? DEFAULT_SETTINGS.contactBadge),
    faqBadge: raw?.faqBadge ?? (missingFallback ?? DEFAULT_SETTINGS.faqBadge),
    legalBadges: raw?.legalBadges ?? (missingFallback ?? DEFAULT_SETTINGS.legalBadges),
    footerWarrantyBadges: raw?.footerWarrantyBadges ?? (missingFallback ?? DEFAULT_SETTINGS.footerWarrantyBadges),
    ...(raw ?? {}),
  };
}

export function BadgeVisibilityProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<BadgeVisibilitySettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? normalizeSettings(JSON.parse(stored) as Partial<BadgeVisibilitySettings>) : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const setBadgeVisibility = (key: BadgeVisibilityKey, value: boolean) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const setAllBadges = (value: boolean) => {
    setSettings({
      heroBadge: value,
      aboutBadge: value,
      animationBadge: value,
      testimonialsBadge: value,
      launchBadges: value,
      productBadges: value,
      wikiBadges: value,
      contactBadge: value,
      faqBadge: value,
      legalBadges: value,
      footerWarrantyBadges: value,
    });
  };

  const resetBadgeVisibility = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const allEnabled = useMemo(
    () => Object.values(settings).every(Boolean),
    [settings],
  );

  return (
    <BadgeVisibilityContext.Provider
      value={{
        settings,
        allEnabled,
        setBadgeVisibility,
        setAllBadges,
        resetBadgeVisibility,
      }}
    >
      {children}
    </BadgeVisibilityContext.Provider>
  );
}

export function useBadgeVisibility() {
  const ctx = useContext(BadgeVisibilityContext);

  if (!ctx) {
    throw new Error('useBadgeVisibility must be used within BadgeVisibilityProvider');
  }

  return ctx;
}
